<template>
  <WdsStack direction-column justify-center gap-xs>
    <div v-tooltip="statusTooltip">
      <WdsLabel
        xs
        subtle
        :title="statusTooltip ? '' : undefined"
        :success="isSuccess"
        :warning="isWarning"
        :error="isError"
        :gray="isGray"
      >
        {{ statusText }}
      </WdsLabel>
    </div>
    <PopoverBillPublicApi :global-id="column.id" :bill-number="String(column.number)" />
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsLabel, WdsStack } from '@worksome/wds-components'
import PopoverBillPublicApi from '@/components/PopoverBillPublicApi.vue'
import { PaymentRequestStatus } from '@/gql/graphql'
import { FragmentType, graphql, useFragment } from '@/gql'
import { useTranslation } from '@/composables/translation'

const PaymentRequestStatusIdColumn = graphql(`
  fragment PaymentRequestStatusIdColumn on PaymentRequest {
    id
    number
    status
    manuallyApprovedAt
    approvedBy {
      id
      name
    }
    rejectedAt
  }
`)

const props = defineProps<{
  row: FragmentType<typeof PaymentRequestStatusIdColumn>
}>()

const { $t } = useTranslation()
const column = computed(() => useFragment(PaymentRequestStatusIdColumn, props.row))

const status = computed(() => column.value.status)

const statusText = computed(() => {
  switch (status.value) {
    case PaymentRequestStatus.Approved:
      return $t('Approved')
    case PaymentRequestStatus.Cancelled:
      return $t('Cancelled')
    case PaymentRequestStatus.Rejected:
      return $t('Rejected')
    case PaymentRequestStatus.Unapproved:
      return $t('Pending')
    default:
      return status.value
  }
})

const isSuccess = computed(() => status.value === PaymentRequestStatus.Approved)
const isWarning = computed(() => status.value === PaymentRequestStatus.Unapproved)
const isError = computed(() => status.value === PaymentRequestStatus.Rejected)
const isGray = computed(() => status.value === PaymentRequestStatus.Cancelled)

const statusTooltip = computed(() => {
  if (status.value === PaymentRequestStatus.Approved) {
    const approverName = column.value.approvedBy?.name
    const approvedAt = column.value.manuallyApprovedAt

    if (approverName && approvedAt) {
      return $t('Approved by :name on :date', {
        name: approverName,
        date: approvedAt,
      })
    }
  }

  if (status.value === PaymentRequestStatus.Rejected) {
    const { rejectedAt } = column.value

    if (rejectedAt) {
      return $t('Rejected on :date', { date: rejectedAt })
    }
  }

  return null
})
</script>
